/**
 * Pick Your Flavors Box — picker UI + purchase button state.
 */

const FLAVOR_BOX_PATH = '/products/pick-your-flavors-box';
const ADD_LABEL_FALLBACK = 'Add';
const ADDED_LABEL_FALLBACK = 'Added';
const DEFAULT_FLAVORS = [
  'VANILLA CRÈME',
  'COCONUT DREAM',
  'TROPICAL TEMPATATION',
  'CITRUS SYMPHONY',
  'CRÈME BRÛLÉE',
  'TIRAMISU',
  'HONEY GINGER SHOT',
  'HAZELNUT PRALINE',
  'JUST CRUNCHY',
  'PISTACHIO CRUNCHES',
  'SALTY KISS',
  'ALMOND PRALINE',
  'CLASSICS',
  'BERRIES CHEESECAKE',
  'CINNAMON ROLL',
  'ESPRESSO CREAM',
  'LE BUENO',
  'LOTUS BLISS',
  "It's Fudgy",
  "COOKIES 'N' CREAM",
];

let syncTimer = null;

function isFlavorBoxPage() {
  return window.location.pathname.includes(FLAVOR_BOX_PATH);
}

function getSection(root = document) {
  if (root instanceof Element) return root.closest('.shopify-section');
  const picker = document.querySelector('[data-boch-flavor-picker]');
  if (picker) return picker.closest('.shopify-section');
  return document.querySelector('product-form-component')?.closest('.shopify-section') ?? null;
}

function getPickerRoot(node) {
  return node?.closest('[data-boch-flavor-picker]');
}

function getVariantPicker(section) {
  return section?.querySelector('variant-picker') ?? null;
}

function getFlavorInputs(section) {
  if (!section) return [];
  const native = [...section.querySelectorAll('.boch-flavor-picker__input[name="properties[Flavors][]"]')];
  if (native.length) return native;

  const tpo = [...section.querySelectorAll('input.tpo_option-input:checked')].filter((input) => {
    const container = input.closest('.tpo_option, [class*="tpo_"], .tpo_options_container');
    const label = container?.textContent || '';
    const name = input.name || '';
    const value = input.value || '';
    return /flavor|flavour/i.test(label) || /flavor|flavour/i.test(name) || /flavor|flavour/i.test(value);
  });
  if (tpo.length) return tpo;

  return [...section.querySelectorAll('input[name*="Flavors"]:checked, input[name*="flavors"]:checked')];
}

function getVariantLabel(variantPicker) {
  if (!variantPicker) return '';
  const radio = variantPicker.querySelector('fieldset input:checked');
  if (radio instanceof HTMLInputElement) return radio.value;
  const select = variantPicker.querySelector('.variant-option--dropdowns select');
  if (select instanceof HTMLSelectElement) return select.value;
  return '';
}

function getRequiredCount(variantPicker, variantIdValue = '') {
  const label = getVariantLabel(variantPicker);
  if (label.includes('25')) return 5;
  if (label.includes('16')) return 4;
  if (variantIdValue === '50814428184882') return 5;
  if (variantIdValue === '50814428152114') return 4;
  return null;
}

function hasVariantSelected(section) {
  const variantId = section?.querySelector('product-form-component input[name="id"]');
  if (!(variantId instanceof HTMLInputElement) || !variantId.value) return false;

  const variantPicker = getVariantPicker(section);
  if (!variantPicker) return true;
  if (variantPicker.hasAllOptionsSelected) return true;
  if (variantPicker.querySelector('fieldset input:checked')) return true;

  const select = variantPicker.querySelector('.variant-option--dropdowns select');
  if (select instanceof HTMLSelectElement && select.selectedIndex > 0) return true;

  const label = getVariantLabel(variantPicker);
  return label.includes('16') || label.includes('25');
}

function isReady(section) {
  if (!section) return false;
  if (!hasVariantSelected(section)) return false;

  const variantPicker = getVariantPicker(section);
  const variantId = section.querySelector('product-form-component input[name="id"]');
  const variantIdValue = variantId instanceof HTMLInputElement ? variantId.value : '';
  const required = getRequiredCount(variantPicker, variantIdValue);
  if (!required) return false;

  return getFlavorInputs(section).filter((input) => input.checked).length === required;
}

function revealButtons(section) {
  if (!section) return;

  section.querySelectorAll('add-to-cart-component, .accelerated-checkout-block').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.removeAttribute('hidden');
    node.style.removeProperty('display');
    node.style.removeProperty('visibility');
  });
}

function setWalletEnabled(section, enabled) {
  if (!enabled) return;

  section.querySelectorAll('.accelerated-checkout-block').forEach((block) => {
    if (!(block instanceof HTMLElement)) return;
    block.removeAttribute('inert');
    block.style.pointerEvents = '';
  });

  if (window.Shopify?.PaymentButton?.init) {
    try {
      window.Shopify.PaymentButton.init();
    } catch (error) {}
  }
}

function showFlavorError(section) {
  const productForm = section?.querySelector('product-form-component');
  if (productForm instanceof HTMLElement && typeof productForm.validateVariantSelectionBeforeAdd === 'function') {
    productForm.validateVariantSelectionBeforeAdd();
    return;
  }

  const message = section?.querySelector('[data-flavor-message]');
  if (message instanceof HTMLElement) {
    message.textContent = 'Please select your box size and the required number of flavors first.';
    message.classList.add('boch-flavor-picker__message--warning');
  }
}

function setButtonsEnabled(section, enabled) {
  if (!section) return;

  revealButtons(section);

  section.querySelectorAll('add-to-cart-component').forEach((container) => {
    if (!(container instanceof HTMLElement)) return;
    const button = container.querySelector('button[name="add"]');
    container.classList.toggle('purchase-locked', !enabled);
    if (enabled) {
      delete container.dataset.bochPurchaseLocked;
    } else {
      container.dataset.bochPurchaseLocked = 'true';
    }

    if (button instanceof HTMLButtonElement) {
      button.disabled = !enabled;
      button.toggleAttribute('aria-disabled', !enabled);
    }
  });

  section.querySelectorAll('.accelerated-checkout-block').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.classList.toggle('purchase-locked', !enabled);
    node.removeAttribute('inert');
    node.style.pointerEvents = '';
    if (enabled) {
      delete node.dataset.bochPurchaseLocked;
      node.removeAttribute('aria-disabled');
    } else {
      node.dataset.bochPurchaseLocked = 'true';
      node.setAttribute('aria-disabled', 'true');
    }
  });

  if (enabled) {
    setWalletEnabled(section, true);
  }
}

function getLabels(picker) {
  return {
    add: picker?.dataset.labelAdd || ADD_LABEL_FALLBACK,
    added: picker?.dataset.labelAdded || ADDED_LABEL_FALLBACK,
  };
}

function syncFlavorAddButton(item) {
  const picker = getPickerRoot(item);
  const { add, added } = getLabels(picker);
  const checkbox = item.querySelector('.boch-flavor-picker__input');
  const button = item.querySelector('[data-flavor-add]');
  if (!(checkbox instanceof HTMLInputElement) || !(button instanceof HTMLButtonElement)) return;

  const isSelected = checkbox.checked;
  button.textContent = isSelected ? added : add;
  button.classList.toggle('boch-flavor-picker__add-btn--added', isSelected);
  button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
}

function syncAllAddButtons(root = document) {
  const picker = root instanceof Document ? document.querySelector('[data-boch-flavor-picker]') : getPickerRoot(root);
  picker?.querySelectorAll('[data-flavor-item]').forEach((item) => {
    syncFlavorAddButton(item);
  });
}

function updatePickerUi(section) {
  const picker = section?.querySelector('[data-boch-flavor-picker]');
  if (!picker) return;

  const variantPicker = getVariantPicker(section);
  const variantId = section.querySelector('product-form-component input[name="id"]');
  const variantIdValue = variantId instanceof HTMLInputElement ? variantId.value : '';
  const required = getRequiredCount(variantPicker, variantIdValue) ?? 0;
  const inputs = getFlavorInputs(section);
  const selected = inputs.filter((input) => input.checked).length;
  const remaining = Math.max(0, required - selected);

  const progress = picker.querySelector('[data-flavor-progress]');
  const message = picker.querySelector('[data-flavor-message]');

  if (progress) {
    let text = 'Select a box size first (16 Pieces or 25 Pieces).';
    if (hasVariantSelected(section)) {
      if (required > 0 && selected >= required) text = `${required} items selected`;
      else if (remaining > 0) text = `Add ${remaining} item${remaining === 1 ? '' : 's'}`;
    }
    progress.textContent = text;
  }

  if (message) {
    let text = '';
    if (hasVariantSelected(section) && selected > 0 && selected < required) {
      text = `${remaining} more flavor${remaining === 1 ? '' : 's'} to complete your box`;
    } else if (selected === required && required > 0) {
      text = 'Ready to add to cart';
    }
    message.textContent = text;
  }

  const atMax = required > 0 && selected >= required;
  inputs.forEach((input) => {
    const shouldDisable = atMax && !input.checked;
    input.disabled = shouldDisable;
    const addBtn = input.closest('[data-flavor-item]')?.querySelector('[data-flavor-add]');
    if (addBtn instanceof HTMLButtonElement) addBtn.disabled = shouldDisable;
  });

  syncAllAddButtons(picker);
}

function syncNow() {
  if (!isFlavorBoxPage()) return;

  const section = getSection();
  if (!section) return;

  revealButtons(section);
  updatePickerUi(section);

  const ready = isReady(section);
  setButtonsEnabled(section, ready);

  const stack = section.querySelector('.buy-buttons-block__purchase-stack, .product-form-buttons');
  stack?.classList.toggle('boch-purchase-ready', ready);
  document.documentElement.classList.toggle('boch-purchase-ready', ready);
}

function scheduleSync() {
  if (syncTimer != null) window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => {
    syncTimer = null;
    syncNow();
  }, 30);
}

function hideEasifyOptions() {
  document.documentElement.classList.add('boch-native-flavors');
  document
    .querySelectorAll(
      '.tpo_option-set-wrapper, .tpo_option-set-container, .tpo_options_container, .tpo-product-options, easify-product-options, [class*="tpo_"]'
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement) || node.dataset.bochTpoHidden === 'true') return;
      node.dataset.bochTpoHidden = 'true';
      node.setAttribute('hidden', '');
      node.style.display = 'none';
    });
}

function injectStyles() {
  if (document.getElementById('boch-flavor-box-inline-styles')) return;
  const style = document.createElement('style');
  style.id = 'boch-flavor-box-inline-styles';
  style.textContent =
    'add-to-cart-component[hidden],.accelerated-checkout-block[hidden]{display:block!important;visibility:visible!important}' +
    '.boch-native-flavors add-to-cart-component,.boch-native-flavors .accelerated-checkout-block{display:block!important;visibility:visible!important;width:100%!important;min-height:var(--height-buy-buttons,48px)}' +
    '.boch-native-flavors .product-form-buttons{display:flex!important;flex-direction:column!important;gap:12px!important;width:100%!important}' +
    '.boch-native-flavors .product-form-buttons add-to-cart-component{order:1}' +
    '.boch-native-flavors .product-form-buttons .accelerated-checkout-block{order:2}' +
    '.buy-buttons-block--native-flavors add-to-cart-component,.buy-buttons-block--native-flavors .accelerated-checkout-block{display:block!important;visibility:visible!important;width:100%!important;min-height:var(--height-buy-buttons,48px)}' +
    '.buy-buttons-block--native-flavors .product-form-buttons{display:flex!important;flex-direction:column!important;gap:12px!important;width:100%!important}' +
    '.boch-native-flavors .boch-flavor-picker__details[open] .boch-flavor-picker__panel,.buy-buttons-block--native-flavors .boch-flavor-picker__details[open] .boch-flavor-picker__panel{max-height:min(70vh,640px);overflow-y:auto}' +
    'add-to-cart-component.purchase-locked,.accelerated-checkout-block.purchase-locked{pointer-events:none}' +
    'add-to-cart-component.purchase-locked .add-to-cart-button,.accelerated-checkout-block.purchase-locked .shopify-payment-button__button{' +
    'background:var(--color-secondary-button-background)!important;color:var(--color-secondary-button-text)!important;' +
    'border-color:var(--color-secondary-button-border)!important;opacity:.5!important;cursor:not-allowed!important}' +
    '.boch-purchase-ready .add-to-cart-button:not(:disabled){background:#000!important;color:#fff!important;border-color:#000!important;opacity:1!important}' +
    '.boch-purchase-ready .shopify-payment-button__button:not([disabled]){background:#000!important;color:#fff!important;border-color:#000!important;opacity:1!important}';
  document.head.appendChild(style);
}

function getPickerImageUrls() {
  const picker = document.querySelector('[data-boch-flavor-picker]');
  return {
    productImage: picker?.dataset.productImage || '',
    flavorImage: picker?.dataset.flavorImage || picker?.dataset.productImage || '',
  };
}

function buildFlavorItemMediaMarkup(flavorImageUrl, flavorName) {
  if (!flavorImageUrl) {
    return '<span class="boch-flavor-picker__item-media"><span class="boch-flavor-picker__item-placeholder"></span></span>';
  }

  const alt = flavorName.replace(/"/g, '&quot;');
  return (
    `<span class="boch-flavor-picker__item-media">` +
    `<img src="${flavorImageUrl}" alt="${alt}" class="boch-flavor-picker__item-image" loading="lazy" width="80" height="80">` +
    `</span>`
  );
}

function buildFallbackPickerHTML() {
  const { productImage, flavorImage } = getPickerImageUrls();
  const thumbMarkup = productImage
    ? `<span class="boch-flavor-picker__thumb"><img src="${productImage}" alt="Choose your flavors" class="boch-flavor-picker__thumb-image" loading="lazy" width="60" height="60"></span>`
    : '';
  const items = DEFAULT_FLAVORS.map((flavorName) => {
    const id = `boch-flavor-${flavorName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
      `<li class="boch-flavor-picker__item" data-flavor-item>` +
      buildFlavorItemMediaMarkup(flavorImage, flavorName) +
      `<span class="boch-flavor-picker__item-info"><span class="boch-flavor-picker__item-name">${flavorName}</span></span>` +
      `<input type="checkbox" class="boch-flavor-picker__input visually-hidden" name="properties[Flavors][]" value="${flavorName.replace(/"/g, '&quot;')}" id="${id}">` +
      `<button type="button" class="boch-flavor-picker__add-btn button" data-flavor-add aria-pressed="false">${ADD_LABEL_FALLBACK}</button>` +
      `</li>`
    );
  }).join('');

  const dataAttrs =
    (productImage ? ` data-product-image="${productImage}"` : '') +
    (flavorImage ? ` data-flavor-image="${flavorImage}"` : '');

  return (
    `<div class="boch-flavor-picker" data-boch-flavor-picker${dataAttrs} data-label-add="${ADD_LABEL_FALLBACK}" data-label-added="${ADDED_LABEL_FALLBACK}">` +
    `<details class="boch-flavor-picker__details">` +
    `<summary class="boch-flavor-picker__summary" aria-expanded="false">` +
    `<span class="boch-flavor-picker__chevron" aria-hidden="true"><svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg></span>` +
    thumbMarkup +
    `<span class="boch-flavor-picker__heading">` +
    `<span class="boch-flavor-picker__bundle-title">Choose your flavors</span>` +
    `<span class="boch-flavor-picker__subtitle" data-flavor-progress aria-live="polite">Select a box size first (16 Pieces or 25 Pieces).</span>` +
    `</span></summary>` +
    `<div class="boch-flavor-picker__panel">` +
    `<p class="boch-flavor-picker__message" data-flavor-message aria-live="polite"></p>` +
    `<ul class="boch-flavor-picker__list" role="list">${items}</ul>` +
    `</div></details></div>`
  );
}

function mountFallbackPicker() {
  const form = document.querySelector('form[id*="BuyButtons-ProductForm"]');
  const buttons = form?.querySelector('.product-form-buttons');
  if (!form || !buttons || document.querySelector('[data-boch-flavor-picker]')) return false;

  const wrapper = document.createElement('div');
  wrapper.className = 'buy-buttons-block__flavors';
  wrapper.innerHTML = buildFallbackPickerHTML();
  form.insertBefore(wrapper, buttons);
  return true;
}

async function ensurePickerMounted() {
  if (document.querySelector('[data-boch-flavor-picker]')) return true;

  const formComponent = document.querySelector('product-form-component[data-section-id]');
  const sectionId = formComponent?.dataset.sectionId;
  if (sectionId) {
    try {
      const response = await fetch(
        `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}&_=${Date.now()}`
      );
      const data = await response.json();
      const html = data?.[sectionId];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const picker = doc.querySelector('[data-boch-flavor-picker]');
        if (picker) {
          const form = document.querySelector('form[id*="BuyButtons-ProductForm"]');
          const buttons = form?.querySelector('.product-form-buttons');
          if (form && buttons) {
            const css = doc.querySelector('link[href*="boch-flavor-picker.css"]');
            if (css && !document.querySelector('link[href*="boch-flavor-picker.css"]')) {
              document.head.appendChild(css.cloneNode(true));
            }

            doc.querySelectorAll('add-to-cart-component[hidden], .accelerated-checkout-block[hidden]').forEach((node) => {
              node.removeAttribute('hidden');
            });

            const wrapper = document.createElement('div');
            wrapper.className = 'buy-buttons-block__flavors';
            wrapper.appendChild(document.importNode(picker, true));
            form.insertBefore(wrapper, buttons);
            hideEasifyOptions();
            initBochFlavorPicker();
            return true;
          }
        }
      }
    } catch (error) {
      console.error('[boch-flavor-picker]', error);
    }
  }

  if (mountFallbackPicker()) {
    hideEasifyOptions();
    initBochFlavorPicker();
    return true;
  }

  return false;
}

function handleFlavorAddClick(event) {
  const button = event.target.closest('[data-flavor-add]');
  if (!(button instanceof HTMLButtonElement)) return;

  const checkbox = button.closest('[data-flavor-item]')?.querySelector('.boch-flavor-picker__input');
  if (!(checkbox instanceof HTMLInputElement)) return;

  event.preventDefault();
  event.stopPropagation();

  if (checkbox.checked) checkbox.checked = false;
  else if (!checkbox.disabled) checkbox.checked = true;

  checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  scheduleSync();
}

function initBochFlavorPicker() {
  const picker = document.querySelector('[data-boch-flavor-picker]');
  if (!picker || picker.dataset.bochFlavorPickerInit === 'true') return;

  picker.dataset.bochFlavorPickerInit = 'true';
  picker.addEventListener('click', handleFlavorAddClick);

  const details = picker.querySelector('.boch-flavor-picker__details');
  const summary = picker.querySelector('.boch-flavor-picker__summary');
  details?.addEventListener('toggle', () => {
    summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  });

  syncAllAddButtons(picker);
}

function initFlavorBoxPurchase() {
  if (window.__bochFlavorBoxInline || !isFlavorBoxPage()) return;
  window.__bochFlavorBoxInline = true;

  const bootHandled = Boolean(window.__bochFlavorBoxBooted);

  injectStyles();
  hideEasifyOptions();

  window.__bochUpdatePickerUi = updatePickerUi;
  window.__bochAreFlavorsValid = (section) => isReady(section || getSection());
  window.__bochGetRequiredFlavorCount = (variantPicker, variantIdValue = '') =>
    getRequiredCount(variantPicker, variantIdValue);

  if (!bootHandled) {
    window.__bochSyncPurchaseState = syncNow;

    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        if (target.closest('variant-picker')) {
          getFlavorInputs(getSection()).forEach((input) => {
            input.checked = false;
          });
          scheduleSync();
          return;
        }

        if (target.matches('.boch-flavor-picker__input') || target.matches('.tpo_option-input')) {
          const section = getSection();
          const max = getRequiredCount(getVariantPicker(section));
          if (max) {
            const checked = getFlavorInputs(section).filter((input) => input.checked);
            if (checked.length > max) target.checked = false;
          }
          scheduleSync();
        }
      },
      true
    );

    document.addEventListener('variant:update', scheduleSync, true);
  }

  window.__bochSyncFlavorAddButtons = syncAllAddButtons;

  ensurePickerMounted().then(() => {
    hideEasifyOptions();
    initBochFlavorPicker();
    if (typeof window.__bochSyncPurchaseState === 'function') {
      window.__bochSyncPurchaseState();
    } else {
      syncNow();
    }
  });

  initBochFlavorPicker();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFlavorBoxPurchase, { once: true });
} else {
  initFlavorBoxPurchase();
}

export { syncAllAddButtons, syncNow as syncPurchaseState };
